import { queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID, RETRIEVE_BY_QUERY, useErrorHelper } from '@inpart-io/devtools-vue';
import { type IDirectoryCompany, type INexusCompany, type IPrivateCompany, privateCompaniesManager, privateCompanyProperties } from '@inpart-io/padme-iap';
import { type Ref, ref } from 'vue';
import { useStore } from 'vuex';

import { PRIVATE_COMPANY_MODULE } from '@/store/private-company-module';

interface CompanyHelperOptions {
    excludeDirectoryCompany?: boolean;
}

export function useCompanyHelper(params?: CompanyHelperOptions): {
    loading: Ref<boolean>;
    nexusSearch: (searchTerm: string) => Promise<{ results: INexusCompany[]; exactMatch: boolean }>;
    creationSearch: (searchTerm: string) => Promise<{ results: Array<IPrivateCompany | IDirectoryCompany>; exactMatch: boolean }>;
} {
    const store = useStore();
    const errorHelper = useErrorHelper();
    const loading = ref(false);
    const { excludeDirectoryCompany = false } = params ?? {};

    /**
     * Perform a typeahead search for private and directory companies
     * @param searchTerm
     * @returns
     */
    const doTypeaheadSearch = async (searchTerm: string): Promise<Array<IPrivateCompany | IDirectoryCompany>> => {
        try {
            if (!searchTerm) {
                return [];
            }
            loading.value = true;
            let result: Array<IPrivateCompany | IDirectoryCompany>;
            if (excludeDirectoryCompany) {
                const suggestions = await privateCompaniesManager.typeahead(searchTerm, 10, null, privateCompanyProperties.DEFAULT);
                result =
                    suggestions?.map((suggestion) => {
                        return {
                            ...suggestion.dataObject
                        };
                    }) ?? [];
            } else {
                result = await privateCompaniesManager.privateAndDirectoryTypeAhead(searchTerm, 10, {
                    properties: privateCompanyProperties.DEFAULT
                });
            }
            return result;
        } catch (error) {
            errorHelper.handleError(error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Perform a typeahead search for nexus companies, currently only cortellis data
     */
    const doNexusTypeaheadSearch = async (searchTerm: string): Promise<INexusCompany[]> => {
        try {
            if (!searchTerm) {
                return [];
            }
            loading.value = true;
            const res = await privateCompaniesManager.getRelevant(searchTerm);
            return res;
        } catch (error) {
            errorHelper.handleError(error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Search for private and directory companies
     * @param searchTerm
     * @returns
     */
    const creationSearch = async (searchTerm: string): Promise<{ results: Array<IPrivateCompany | IDirectoryCompany>; exactMatch: boolean }> => {
        const response = {
            results: [] as Array<IPrivateCompany | IDirectoryCompany>,
            exactMatch: false
        };
        try {
            loading.value = true;
            if (searchTerm?.length > 0) {
                response.results = await doTypeaheadSearch(searchTerm);

                response.exactMatch = response.results.some((company) => {
                    return searchTerm.localeCompare(company.name, undefined, { sensitivity: 'base' }) === 0;
                });
            } else {
                response.results = (
                    await store.dispatch(`${PRIVATE_COMPANY_MODULE}/${RETRIEVE_BY_QUERY}`, {
                        query: queryBuilder.build(undefined, 0, 10, [{ attribute: 'name', direction: 'ASC' }]),
                        properties: privateCompanyProperties.DEFAULT
                    })
                ).map((dobjId: number) => {
                    return store.getters[`${PRIVATE_COMPANY_MODULE}/${GET_BY_ID}`](dobjId);
                });
            }
            return response;
        } catch (error) {
            errorHelper.handleError(error);
        } finally {
            loading.value = false;
        }
    };

    /**
     * Search for nexus companies, currently only cortellis data
     */
    const nexusSearch = async (searchTerm: string): Promise<{ results: INexusCompany[]; exactMatch: boolean }> => {
        const response = {
            results: [] as INexusCompany[],
            exactMatch: false
        };
        try {
            loading.value = true;
            if (searchTerm?.length > 0) {
                response.results = await doNexusTypeaheadSearch(searchTerm);
                response.exactMatch = response.results.some((company) => {
                    return searchTerm.localeCompare(company.name, undefined, { sensitivity: 'base' }) === 0;
                });
            }
            return response;
        } catch (error) {
            errorHelper.handleError(error);
        } finally {
            loading.value = false;
        }
    };

    return { creationSearch, nexusSearch, loading };
}
